import React, { useEffect, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';

// Contexts
import { ThemeProvider } from './contexts/ThemeContext';
import { I18nProvider } from './contexts/I18nContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PrimaryColorProvider } from './contexts/PrimaryColorContext';
import { ExtensionsProvider } from './hooks/useExtensions';

// Theme Color Hook
import { useThemeColor } from './components/ThemeColorSelector';

// Layout
import DashboardLayout from './components/DashboardLayout';

// Pages
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import DirectoryPage from './pages/DirectoryPage';
import OrdersPage from './pages/OrdersPage';
import NewOrderPage from './pages/NewOrderPage';
import OrderDetailPage from './pages/OrderDetailPage';
import CatalogPage from './pages/CatalogPage';
import CatalogsPage from './pages/CatalogsPage';
import ImportCatalogPage from './pages/ImportCatalogPage';
import CatalogImportAIPage from './pages/CatalogImportAIPage';
import PricelistsPage from './pages/PricelistsPage';
import RelationshipsPage from './pages/RelationshipsPage';
import CustomerPricelistPage from './pages/CustomerPricelistPage';
import ChatPage from './pages/ChatPage';
import SettingsPage from './pages/SettingsPage';
import ReportsPage from './pages/ReportsPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import AdminAccreditationPage from './pages/AdminAccreditationPage';
import AdminNotificationsPage from './pages/AdminNotificationsPage';
import AdminExtensionsPage from './pages/AdminExtensionsPage';
import AdminPaymentsPage from './pages/AdminPaymentsPage';
import TeamPage from './pages/TeamPage';
import JoinPage from './pages/JoinPage';
import DeliveryZonesPage from './pages/DeliveryZonesPage';
import DeliveryRoutesPage from './pages/DeliveryRoutesPage';
import NotificationsPage from './pages/NotificationsPage';
import ExtensionStorePage from './pages/ExtensionStorePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import PrivacySettingsPage from './pages/PrivacySettingsPage';
import AdminGDPRPage from './pages/AdminGDPRPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SupplierCatalogPage from './pages/SupplierCatalogPage';
import SellerCreditsPage from './pages/SellerCreditsPage';
import BuyerPaymentsPage from './pages/BuyerPaymentsPage';
import TransactionHistoryPage from './pages/TransactionHistoryPage';
import AdminAssociationsPage from './pages/AdminAssociationsPage';
import GroupagePage from './pages/GroupagePage';
import AssociationDashboardPage from './pages/AssociationDashboardPage';
import GroupageSummaryPage from './pages/GroupageSummaryPage';
import MembersManagementPage from './pages/MembersManagementPage';
import AssociationSettingsPage from './pages/AssociationSettingsPage';
import AssociationSuppliersPage from './pages/AssociationSuppliersPage';
import AssociationRequestsPage from './pages/AssociationRequestsPage';
import AdminVatLookupPage from './pages/AdminVatLookupPage';
import BuyerStatsPage from './pages/BuyerStatsPage';
import SellerStatsPage from './pages/SellerStatsPage';
import ProductsPage from './pages/ProductsPage';
import ServicesPage from './pages/ServicesPage';
import AdminErrorsPage from './pages/AdminErrorsPage';
import AuditLogsPage from './pages/AuditLogsPage';
import CookieConsentBanner from './components/CookieConsentBanner';
import PushNotificationManager from './components/PushNotificationManager';
import PublicProfilePage from './pages/PublicProfilePage';

// Types
interface ProtectedRouteProps {
  children: ReactNode;
}

// Protected Route Component
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return <DashboardLayout>{children}</DashboardLayout>;
};

// Public Route Component (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Main App Routes
const AppRoutes = () => {
  // Inizializza il colore tema salvato
  useThemeColor();
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/directory" element={<ProtectedRoute><DirectoryPage /></ProtectedRoute>} />
      <Route path="/catalog/:supplierId" element={<ProtectedRoute><SupplierCatalogPage /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
      <Route path="/orders/new" element={<ProtectedRoute><NewOrderPage /></ProtectedRoute>} />
      <Route path="/orders/:id" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
      <Route path="/catalog" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
      <Route path="/catalog/import" element={<ProtectedRoute><ImportCatalogPage /></ProtectedRoute>} />
      <Route path="/catalog/import-ai" element={<ProtectedRoute><CatalogImportAIPage /></ProtectedRoute>} />
      <Route path="/catalogs" element={<ProtectedRoute><CatalogsPage /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
      <Route path="/services" element={<ProtectedRoute><ServicesPage /></ProtectedRoute>} />
      <Route path="/pricelists" element={<ProtectedRoute><PricelistsPage /></ProtectedRoute>} />
      <Route path="/relationships" element={<ProtectedRoute><RelationshipsPage /></ProtectedRoute>} />
      <Route path="/relationships/:relationshipId/pricelist" element={<ProtectedRoute><CustomerPricelistPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/admin/accreditation" element={<ProtectedRoute><AdminAccreditationPage /></ProtectedRoute>} />
      <Route path="/admin/notifications" element={<ProtectedRoute><AdminNotificationsPage /></ProtectedRoute>} />
      <Route path="/admin/extensions" element={<ProtectedRoute><AdminExtensionsPage /></ProtectedRoute>} />
      <Route path="/admin/payments" element={<ProtectedRoute><AdminPaymentsPage /></ProtectedRoute>} />
      <Route path="/admin/gdpr" element={<ProtectedRoute><AdminGDPRPage /></ProtectedRoute>} />
      <Route path="/admin/associations" element={<ProtectedRoute><AdminAssociationsPage /></ProtectedRoute>} />
      <Route path="/admin/vat-lookup" element={<ProtectedRoute><AdminVatLookupPage /></ProtectedRoute>} />
      <Route path="/admin/errors" element={<ProtectedRoute><AdminErrorsPage /></ProtectedRoute>} />
      <Route path="/admin/audit-logs" element={<ProtectedRoute><AuditLogsPage /></ProtectedRoute>} />
      <Route path="/privacy-settings" element={<ProtectedRoute><PrivacySettingsPage /></ProtectedRoute>} />
      <Route path="/delivery-zones" element={<ProtectedRoute><DeliveryZonesPage /></ProtectedRoute>} />
      <Route path="/delivery-routes" element={<ProtectedRoute><DeliveryRoutesPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/store" element={<ProtectedRoute><ExtensionStorePage /></ProtectedRoute>} />
      <Route path="/credits" element={<ProtectedRoute><SellerCreditsPage /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><BuyerPaymentsPage /></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute><TransactionHistoryPage /></ProtectedRoute>} />
      <Route path="/stats" element={<ProtectedRoute><BuyerStatsPage /></ProtectedRoute>} />
      <Route path="/seller-stats" element={<ProtectedRoute><SellerStatsPage /></ProtectedRoute>} />
      <Route path="/groupage" element={<ProtectedRoute><GroupagePage /></ProtectedRoute>} />
      <Route path="/groupage/:groupageId/summary" element={<ProtectedRoute><GroupageSummaryPage /></ProtectedRoute>} />
      <Route path="/association/dashboard" element={<ProtectedRoute><AssociationDashboardPage /></ProtectedRoute>} />
      <Route path="/association/members" element={<ProtectedRoute><MembersManagementPage /></ProtectedRoute>} />
      <Route path="/association/settings" element={<ProtectedRoute><AssociationSettingsPage /></ProtectedRoute>} />
      <Route path="/association/suppliers" element={<ProtectedRoute><AssociationSuppliersPage /></ProtectedRoute>} />
      <Route path="/association/requests" element={<ProtectedRoute><AssociationRequestsPage /></ProtectedRoute>} />
      
      {/* Route pubblica per accettare inviti */}
      <Route path="/join/:code" element={<JoinPage />} />
      
      {/* Route pubblica per profili aziendali */}
      <Route path="/p/:slug" element={<PublicProfilePage />} />
      
      {/* Pagine legali pubbliche */}
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookie-policy" element={<CookiePolicyPage />} />
      
      {/* Redirect root to dashboard or auth */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* 404 fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  // Set default brand name
  React.useEffect(() => {
    document.title = 'OPS!';
  }, []);
  return (
    <BrowserRouter>
      <ThemeProvider>
        <PrimaryColorProvider>
          <I18nProvider>
            <AuthProvider>
              <ExtensionsProvider>
                <AppRoutes />
                <CookieConsentBanner />
                <PushNotificationManager />
                <Toaster 
                  position="top-right" 
                  richColors 
                  closeButton
                  toastOptions={{
                    className: 'font-body'
                  }}
                />
              </ExtensionsProvider>
            </AuthProvider>
          </I18nProvider>
        </PrimaryColorProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
